import React, { useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Rating,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Message as MessageIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { DashboardLayout } from '../../components/layout/DashboardLayout';

interface Supplier {
  id: string;
  name: string;
  contactPerson: string;
  email: string;
  phone: string;
  address: string;
  rating: number;
  status: 'active' | 'inactive';
  specialties: string[];
  lastOrder: string;
  totalOrders: number;
}

interface Communication {
  id: string;
  supplierId: string;
  date: string;
  type: 'email' | 'phone' | 'message';
  content: string;
}

export const SupplierManagement: React.FC = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
  const [openCommunication, setOpenCommunication] = useState(false);

  // Mock data - replace with API call
  const suppliers: Supplier[] = [
    {
      id: '1',
      name: 'AutoParts Plus',
      contactPerson: 'John Smith',
      email: 'john@autopartsplus.com',
      phone: '+1 234-567-8900',
      address: '123 Parts Street, Auto City, AC 12345',
      rating: 4.5,
      status: 'active',
      specialties: ['Brakes', 'Suspension', 'Engine Parts'],
      lastOrder: '2024-03-15',
      totalOrders: 156,
    },
    {
      id: '2',
      name: 'Global Parts Co',
      contactPerson: 'Sarah Johnson',
      email: 'sarah@globalparts.com',
      phone: '+1 234-567-8901',
      address: '456 Supply Road, Parts Town, PT 67890',
      rating: 4.0,
      status: 'active',
      specialties: ['Electronics', 'Lighting', 'Sensors'],
      lastOrder: '2024-03-10',
      totalOrders: 89,
    },
  ];

  const communications: Communication[] = [
    {
      id: '1',
      supplierId: '1',
      date: '2024-03-15',
      type: 'email',
      content: 'Order #12345 confirmation received',
    },
    {
      id: '2',
      supplierId: '1',
      type: 'phone',
      date: '2024-03-14',
      content: 'Discussed delivery timeline for pending order',
    },
  ];

  const handleEditClick = (supplier: Supplier) => {
    setSelectedSupplier(supplier);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setSelectedSupplier(null);
    setOpenDialog(false);
  };

  const handleMessageClick = (supplier: Supplier) => {
    setSelectedSupplier(supplier);
    setOpenCommunication(true);
  };

  return (
    <DashboardLayout>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Supplier Management
        </Typography>

        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6">Total Suppliers</Typography>
                <Typography variant="h3">24</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6">Active Suppliers</Typography>
                <Typography variant="h3" color="success.main">20</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6">Pending Orders</Typography>
                <Typography variant="h3" color="warning.main">5</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6">Average Rating</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="h3">4.2</Typography>
                  <StarIcon sx={{ color: 'warning.main', ml: 1 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Paper sx={{ width: '100%', mb: 2 }}>
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between' }}>
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => setOpenDialog(true)}
            >
              Add New Supplier
            </Button>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Supplier</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell>Specialties</TableCell>
                  <TableCell>Rating</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Last Order</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {suppliers.map((supplier) => (
                  <TableRow key={supplier.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar sx={{ mr: 2 }}>{supplier.name[0]}</Avatar>
                        <Box>
                          <Typography variant="subtitle2">{supplier.name}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {supplier.totalOrders} orders
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{supplier.contactPerson}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {supplier.email}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {supplier.specialties.map((specialty) => (
                        <Chip
                          key={specialty}
                          label={specialty}
                          size="small"
                          sx={{ mr: 0.5, mb: 0.5 }}
                        />
                      ))}
                    </TableCell>
                    <TableCell>
                      <Rating value={supplier.rating} readOnly size="small" />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={supplier.status}
                        color={supplier.status === 'active' ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{supplier.lastOrder}</TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleEditClick(supplier)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleMessageClick(supplier)}
                      >
                        <MessageIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Add/Edit Supplier Dialog */}
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            {selectedSupplier ? 'Edit Supplier' : 'Add New Supplier'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Supplier Name"
                  defaultValue={selectedSupplier?.name}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Contact Person"
                  defaultValue={selectedSupplier?.contactPerson}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  defaultValue={selectedSupplier?.email}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  defaultValue={selectedSupplier?.phone}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  multiline
                  rows={2}
                  defaultValue={selectedSupplier?.address}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Specialties"
                  helperText="Enter specialties separated by commas"
                  defaultValue={selectedSupplier?.specialties.join(', ')}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button variant="contained" onClick={handleCloseDialog}>
              {selectedSupplier ? 'Save Changes' : 'Add Supplier'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Communication History Dialog */}
        <Dialog
          open={openCommunication}
          onClose={() => setOpenCommunication(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Communication History</DialogTitle>
          <DialogContent>
            <List>
              {communications.map((comm) => (
                <React.Fragment key={comm.id}>
                  <ListItem alignItems="flex-start">
                    <ListItemAvatar>
                      <Avatar>
                        {comm.type === 'email' ? <EmailIcon /> :
                         comm.type === 'phone' ? <PhoneIcon /> : <MessageIcon />}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={comm.date}
                      secondary={comm.content}
                    />
                  </ListItem>
                  <Divider component="li" />
                </React.Fragment>
              ))}
            </List>
            <Box sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label="New Message"
                multiline
                rows={3}
                placeholder="Type your message here..."
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenCommunication(false)}>Close</Button>
            <Button variant="contained" color="primary">
              Send Message
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </DashboardLayout>
  );
}; 